// ============================================
// AlpenScreen Translation Panel - Enhanced Version
// File: frontend/src/components/ContentStudio/TranslationPanelEnhanced.js
// With Translation Memory, Batch Mode & Smart Suggestions
// ============================================

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Languages,
  Loader,
  Check,
  AlertCircle,
  ChevronDown,
  Copy,
  History,
  Lightbulb,
  Trash2
} from 'lucide-react';
import { 
  getAIPreferences,
  getTranslationMemory,
  addToTranslationMemory,
  getTranslationSuggestion,
  copyToClipboard,
  countCharacters
} from '../../utils/aiPreferences';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
  { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt-PT', name: 'Português', flag: '🇵🇹' }
];

const TranslationPanelEnhanced = ({ design, currentText, onTranslated }) => {
  const { t } = useTranslation();
  
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Form state
  const [mode, setMode] = useState('text'); // 'text' | 'design'
  const [sourceLang, setSourceLang] = useState('auto');
  const [targetLang, setTargetLang] = useState('en-US');
  const [formality, setFormality] = useState('default');

  // Translation memory & suggestions
  const [memorySuggestion, setMemorySuggestion] = useState(null);
  const [memoryCount, setMemoryCount] = useState(0);

  // Usage tracking
  const [usage, setUsage] = useState({ characterCount: 0, characterLimit: 500000 });

  // Get preferences
  const preferences = getAIPreferences();

  // Check availability on mount
  useEffect(() => {
    checkStatus();
  }, []);

  // Update target language from preferences
  useEffect(() => {
    if (preferences.language && preferences.language !== targetLang) {
      const langMap = {
        'de': 'de',
        'en': 'en-US',
        'fr': 'fr',
        'it': 'it'
      };
      setTargetLang(langMap[preferences.language] || 'en-US');
    }
  }, [preferences.language]);

  // Check for translation memory suggestion
  useEffect(() => {
    if (currentText && sourceLang !== 'auto' && mode === 'text') {
      const suggestion = getTranslationSuggestion(currentText, sourceLang, targetLang);
      setMemorySuggestion(suggestion);
    } else {
      setMemorySuggestion(null);
    }
    
    // Update memory count
    const memory = getTranslationMemory();
    let count = 0;
    Object.values(memory).forEach(langPair => {
      count += Object.keys(langPair).length;
    });
    setMemoryCount(count);
  }, [currentText, sourceLang, targetLang, mode]);

  const checkStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/translation/status`);
      const data = await response.json();
      setAvailable(data.configured && data.available);
      if (data.usage) {
        setUsage(data.usage);
      }
    } catch (error) {
      console.error('Error checking translation status:', error);
      setAvailable(false);
    }
  };

  const handleTranslate = async () => {
    if (mode === 'text' && !currentText?.trim()) {
      setError(t('translation.errors.noText', 'Kein Text zum Übersetzen ausgewählt'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      if (mode === 'text') {
        // Text translation
        const response = await fetch(`${API_URL}/translation/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: currentText,
            targetLang,
            sourceLang: sourceLang === 'auto' ? null : sourceLang,
            formality: formality === 'default' ? null : formality
          })
        });

        if (!response.ok) {
          throw new Error('Translation failed');
        }

        const data = await response.json();
        setResult({
          type: 'text',
          translated: data.translated,
          detectedSourceLang: data.detectedSourceLang
        });

        // Add to translation memory
        if (data.detectedSourceLang) {
          addToTranslationMemory(
            currentText,
            data.translated,
            data.detectedSourceLang,
            targetLang
          );
        }

        // Update usage
        if (data.usage) {
          setUsage(data.usage);
        }

      } else {
        // Design translation
        if (!design?.canvas_data) {
          throw new Error('No design data available');
        }

        const response = await fetch(`${API_URL}/translation/translate-design`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            design: design.canvas_data,
            targetLang,
            sourceLang: sourceLang === 'auto' ? null : sourceLang,
            formality: formality === 'default' ? null : formality
          })
        });

        if (!response.ok) {
          throw new Error('Design translation failed');
        }

        const data = await response.json();
        setResult({
          type: 'design',
          translatedDesign: data.translatedDesign,
          textsTranslated: data.textsTranslated
        });

        // Update usage
        if (data.usage) {
          setUsage(data.usage);
        }
      }

    } catch (err) {
      setError(t('translation.errors.failed', 'Übersetzung fehlgeschlagen'));
      console.error('Translation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!result) return;

    if (result.type === 'text' && onTranslated) {
      onTranslated(result.translated);
    } else if (result.type === 'design' && onTranslated) {
      onTranslated(result.translatedDesign);
    }

    setResult(null);
  };

  const handleCopy = async (text) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleApplySuggestion = () => {
    if (memorySuggestion && onTranslated) {
      onTranslated(memorySuggestion);
      setMemorySuggestion(null);
    }
  };

  // Calculate usage percentage
  const usagePercentage = Math.min(
    100,
    (usage.characterCount / usage.characterLimit) * 100
  );

  const getUsageColor = () => {
    if (usagePercentage < 50) return 'bg-green-500';
    if (usagePercentage < 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (!available) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('translation.notConfigured', 'DeepL Übersetzung nicht konfiguriert')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {t('translation.configureHint', 'DEEPL_API_KEY in Umgebungsvariablen setzen')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden mb-4 transition-shadow hover:shadow-md">
      {/* Header - Modern & Spacious */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 dark:hover:from-blue-900/10 dark:hover:to-cyan-900/10 transition-all duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
            <Languages size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('translation.title', 'Übersetzung')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {t('translation.subtitle', 'Powered by DeepL')}
            </p>
          </div>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Content */}
      {isOpen && (
        <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-3">
          {/* Usage Bar */}
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-blue-700 dark:text-blue-400">
                {t('translation.usage', 'Verbrauch')}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {usage.characterCount.toLocaleString()} / {usage.characterLimit.toLocaleString()} ({usagePercentage.toFixed(1)}%)
              </p>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className={`${getUsageColor()} h-2 rounded-full transition-all`}
                style={{ width: `${usagePercentage}%` }}
              />
            </div>
          </div>

          {/* Translation Memory Info */}
          {memoryCount > 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <History size={14} />
              {memoryCount} {t('translation.savedTerms', 'gespeicherte Begriffe')}
            </div>
          )}

          {/* Memory Suggestion */}
          {memorySuggestion && (
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-2">
                <Lightbulb size={14} className="text-green-600 dark:text-green-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">
                    {t('translation.suggestion', 'Vorschlag aus Speicher')}
                  </p>
                  <p className="text-xs text-gray-900 dark:text-white mb-2">
                    {memorySuggestion}
                  </p>
                  <button
                    onClick={handleApplySuggestion}
                    className="w-full py-1 px-2 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                  >
                    {t('translation.applySuggestion', 'Vorschlag verwenden')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mode Selection */}
          <div className="flex gap-2">
            <button
              onClick={() => setMode('text')}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${
                mode === 'text'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('translation.modes.text', 'Text')}
            </button>
            <button
              onClick={() => setMode('design')}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${
                mode === 'design'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('translation.modes.design', 'Komplettes Design')}
            </button>
          </div>

          {/* Current Text Preview (Text Mode) */}
          {mode === 'text' && currentText && (
            <div className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                {t('translation.currentText', 'Aktueller Text')}:
              </p>
              <p className="text-xs text-gray-900 dark:text-white">
                "{currentText}"
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {countCharacters(currentText)} {t('translation.characters', 'Zeichen')}
              </p>
            </div>
          )}

          {/* Language Selection */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('translation.from', 'Von')}
              </label>
              <select
                value={sourceLang}
                onChange={(e) => setSourceLang(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
              >
                <option value="auto">🔍 {t('translation.autoDetect', 'Auto-Erkennung')}</option>
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('translation.to', 'Nach')}
              </label>
              <select
                value={targetLang}
                onChange={(e) => setTargetLang(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Formality */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('translation.formality', 'Formalität')}
            </label>
            <select
              value={formality}
              onChange={(e) => setFormality(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            >
              <option value="default">{t('translation.formality.default', 'Standard')}</option>
              <option value="more">{t('translation.formality.more', 'Formell (Sie)')}</option>
              <option value="less">{t('translation.formality.less', 'Informell (Du)')}</option>
            </select>
          </div>

          {/* Translate Button */}
          <button
            onClick={handleTranslate}
            disabled={loading || (mode === 'text' && !currentText)}
            className="w-full py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-cyan-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          >
            {loading ? <Loader size={16} className="animate-spin" /> : <Languages size={16} />}
            {loading ? t('translation.translating', 'Übersetze...') : t('translation.translate', 'Übersetzen')}
          </button>

          {/* Error */}
          {error && (
            <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Check size={16} className="text-green-600 dark:text-green-400" />
                  <p className="text-xs font-medium text-green-700 dark:text-green-400">
                    {result.type === 'text' 
                      ? t('translation.translated', 'Übersetzt')
                      : t('translation.designTranslated', 'Design übersetzt')}
                  </p>
                </div>
                {result.type === 'text' && (
                  <button
                    onClick={() => handleCopy(result.translated)}
                    className="text-green-600 dark:text-green-400 hover:text-green-700 transition-colors"
                  >
                    {copySuccess ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                )}
              </div>

              {result.type === 'text' && (
                <>
                  {result.detectedSourceLang && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      {result.detectedSourceLang.toUpperCase()} → {targetLang.toUpperCase()}
                    </p>
                  )}
                  <p className="text-sm text-gray-900 dark:text-white mb-2">
                    {result.translated}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {countCharacters(result.translated)} {t('translation.characters', 'Zeichen')}
                  </p>
                </>
              )}

              {result.type === 'design' && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  {result.textsTranslated} {t('translation.textsTranslated', 'Texte übersetzt')}
                </p>
              )}

              <button
                onClick={handleApply}
                className="w-full py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
              >
                {result.type === 'text' 
                  ? t('translation.apply', 'Anwenden')
                  : t('translation.loadTranslated', 'Übersetztes Design laden')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TranslationPanelEnhanced;